// WHICH OFFICE AREAS A PRINCIPAL IS OFFERED (D-586).
//
// The office nav used to decide each area inside the shell component, one hook
// at a time, from mirrors that mapped the caller's LITERAL role codes through
// the seed roles' grants. A tenant's clone of a seed role (spec 16 §3.9.1)
// matched no row, so its user signed in to an office with no areas at all, and
// a role the tenant had trimmed was offered every area its source role holds.
//
// This is the one pure rule the nav renders. It reads the role signal the feed
// now carries: the permission codes the roles hold, for each area's gate, and
// the roles' lineage roots, for the few audiences §4 writes as role sets (the
// sales books). So a clone is offered exactly what its source role is offered,
// a trimmed role is offered less, and a unit test can say both without
// rendering the shell. The Agents entry is not here: it shows only when
// `GET /v1/agents` returns a row this reader may see (D-529 ruling 1), which no
// role fact can predict.

import type { RoleSignal } from "../lib/role-codes";
import { deriveOnboardingPermissions } from "../areas/onboarding/hooks/permissions";
import { deriveCataloguePermissions } from "../areas/office/catalogue/hooks/permissions";
import { deriveInventoryPermissions } from "../areas/office/inventory/hooks/permissions";
import { deriveProductionPermissions } from "../areas/office/production/hooks/permissions";
import {
  canReadWasteSettings,
  deriveWastePermissions,
  wasteLandingPath,
} from "../areas/office/waste/hooks/permissions";
import {
  deriveQualityPermissions,
  qualityLandingPath,
} from "../areas/office/quality/hooks/permissions";
import {
  deriveSalesAudiences,
  deriveSalesPermissions,
  salesLandingPath,
} from "../areas/office/sales/hooks/permissions";
import {
  deriveMessagingPermissions,
  messagingLandingPath,
} from "../areas/office/messaging/hooks/permissions";
import {
  derivePlanningPermissions,
  planningLandingPath,
} from "../areas/office/planning/hooks/permissions";
import {
  AUDIT_READ_PERMISSION,
  deriveIdentityPermissions,
  hasUnknownRole,
} from "../areas/office/audit/hooks/permissions";

/** An office area the nav may offer, in the nav's own order. */
export type OfficeAreaKey =
  | "onboarding"
  | "catalogue"
  | "inventory"
  | "production"
  | "waste"
  | "quality"
  | "sales"
  | "messaging"
  | "planning"
  | "audit"
  | "terminology"
  | "industry-pack"
  | "floor";

export interface OfficeArea {
  readonly key: OfficeAreaKey;
  /** Where the entry lands: the first screen of the area this reader opens. */
  readonly to: string;
}

/** What the nav knows about the feed beyond the signal itself. */
export interface OfficeAreaOptions {
  /**
   * The role feed FAILED after its retries (`RoleFeed.isFeedError`), as
   * opposed to settling with no grants. The signal is then empty, which
   * says nothing about what the reader may do.
   */
  readonly feedFailed?: boolean;
}

/**
 * The office areas this role signal is offered, in nav order (pure). Each
 * gate is the one its area's own sub-nav and screens read, so an entry never
 * leads into an area whose every screen refuses the reader.
 *
 * A FAILED feed keeps the two entries that were offered to everyone before
 * D-586, Catalogue and Inventory. Gating them on the feed turned a network
 * failure into a nav of Home alone, which is "we cannot tell" read as "you
 * may not" (lib/role-codes.ts, defect 1). Every area beyond those two still
 * waits for the feed, as it always did, and the server still refuses
 * whatever the reader does not hold.
 */
export function officeAreas(roles: RoleSignal, options: OfficeAreaOptions = {}): OfficeArea[] {
  const out: OfficeArea[] = [];
  const has = (codes: ReadonlySet<string>) => (code: string) => codes.has(code);
  const feedFailed = options.feedFailed === true;

  const onboarding = deriveOnboardingPermissions(roles);
  if (onboarding.has("onboarding.view")) out.push({ key: "onboarding", to: "/onboarding" });

  if (feedFailed || deriveCataloguePermissions(roles).has("catalogue.read")) {
    out.push({ key: "catalogue", to: "/catalogue/products" });
  }

  const inventory = deriveInventoryPermissions(roles);
  if (feedFailed || inventory.has("inventory.read")) {
    out.push({ key: "inventory", to: "/inventory/lookup" });
  }

  if (deriveProductionPermissions(roles).has("production.read")) {
    out.push({ key: "production", to: "/production/work-orders" });
  }

  const waste = deriveWastePermissions(roles);
  const wasteTo = wasteLandingPath({
    has: has(waste),
    canReadSettings: canReadWasteSettings(roles, waste),
  });
  if (wasteTo !== undefined) out.push({ key: "waste", to: wasteTo });

  const qualityTo = qualityLandingPath({ has: has(deriveQualityPermissions(roles)) });
  if (qualityTo !== undefined) out.push({ key: "quality", to: qualityTo });

  const sales = deriveSalesPermissions(roles);
  const salesTo = salesLandingPath({ has: has(sales), ...deriveSalesAudiences(roles, sales) });
  if (salesTo !== undefined) out.push({ key: "sales", to: salesTo });

  const messaging = deriveMessagingPermissions(roles);
  const messagingTo = messagingLandingPath({
    canOpenInbox: messaging.has("messaging.conversation.read"),
    canOpenTemplates: messaging.has("messaging.template.read"),
    canOpenSettings: messaging.has("messaging.settings.read"),
  });
  if (messagingTo !== undefined) out.push({ key: "messaging", to: messagingTo });

  const planningTo = planningLandingPath({ has: has(derivePlanningPermissions(roles)) });
  if (planningTo !== undefined) out.push({ key: "planning", to: planningTo });

  if (deriveIdentityPermissions(roles).has(AUDIT_READ_PERMISSION) || hasUnknownRole(roles)) {
    out.push({ key: "audit", to: "/audit" });
  }

  if (onboarding.has("onboarding.edit_terminology")) {
    out.push({ key: "terminology", to: "/settings/terminology" });
  }
  if (onboarding.has("onboarding.install_pack")) {
    out.push({ key: "industry-pack", to: "/settings/industry-pack" });
  }

  if (inventory.has("inventory.scan.locate") || inventory.has("inventory.count.perform")) {
    out.push({ key: "floor", to: "/floor" });
  }
  return out;
}
